const BASE = '';

async function request(url, options = {}) {
  const res = await fetch(`${BASE}${url}`, {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    credentials: 'same-origin',
    ...options,
  });

  if (res.status === 401) {
    window.location.href = '/auth/login';
    return null;
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || `Request failed: ${res.status}`);
    err.responseBody = body;
    throw err;
  }

  return res.json();
}

// File upload helper (no Content-Type header – browser sets multipart boundary)
async function uploadFile(url, formData) {
  const res = await fetch(`${BASE}${url}`, {
    method: 'POST',
    credentials: 'same-origin',
    body: formData,
  });

  if (res.status === 401) {
    window.location.href = '/auth/login';
    return null;
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || `Upload failed: ${res.status}`);
    err.responseBody = body;
    err._debug = body._debug || null;
    throw err;
  }

  return res.json();
}

const api = {
  // Auth
  getMe: () => request('/auth/me'),
  setPreferences: (prefs) => request('/auth/preferences', { method: 'POST', body: JSON.stringify(prefs) }),
  // Dashboard panels backed by the app database
  getDashboardExpenses: () => request('/api/dashboard/expenses-to-code'),
  getDashboardBillableByMonth: () => request('/api/dashboard/billable-by-month'),
  getDashboardBillableJobs: () => request('/api/dashboard/billable-jobs-by-timesheet'),
  getDashboardRateExceptions: () => request('/api/dashboard/rate-exceptions'),
  logout: () => request('/auth/logout', { method: 'POST' }),


  // Reference data
  getCustomers: () => request('/api/customers'),
  getJobs: (customerId) =>
    request(`/api/jobs${customerId ? `?customerId=${customerId}` : ''}`),
  getJobTasks: (jobId, opts = {}) => {
    const params = new URLSearchParams();
    if (jobId) params.set('jobId', jobId);
    if (opts.taskCode) params.set('taskCode', opts.taskCode);
    if (opts.excludeTaskCode) params.set('excludeTaskCode', opts.excludeTaskCode);
    const qs = params.toString();
    return request(`/api/job-tasks${qs ? `?${qs}` : ''}`);
  },
  getRateCodes: (rateSheetCode, rateType) => {
    const params = new URLSearchParams();
    if (rateSheetCode) params.set('rateSheetCode', rateSheetCode);
    if (rateType) params.set('rateType', rateType);
    const qs = params.toString();
    return request(`/api/rate-codes${qs ? `?${qs}` : ''}`);
  },

  // Timesheets
  getTimesheets: async (page = 1, pageSize = 25) => {
    const data = await request(`/api/timesheets?page=${page}&pageSize=${pageSize}`);
    // Server returns { timesheets, total, page, pageSize, _navDebug } (or, for
    // backwards compat, a plain array). Normalize to that object shape.
    if (Array.isArray(data)) {
      return { timesheets: data, total: data.length, page: 1, pageSize: data.length || pageSize, _navDebug: data._navDebug };
    }
    return data;
  },
  getTimesheetPeriods: () => request('/api/timesheets/periods'),
  getTimesheet: (id) => request(`/api/timesheets/${id}`),
  // date ('YYYY-MM-DD') picks a specific period; omit it for the current period,
  // which the server determines from its own clock.
  getOrCreateTimesheet: (date) =>
    request('/api/timesheets', { method: 'POST', body: JSON.stringify(date ? { date } : {}) }),
  submitTimesheet: (id) =>
    request(`/api/timesheets/${id}/submit`, { method: 'PUT' }),
  reopenTimesheet: (id) =>
    request(`/api/timesheets/${id}/reopen`, { method: 'PUT' }),
  getBillableInfo: () => request('/api/timesheets/billable-info'),
  getBillableInfoByJob: () => request('/api/timesheets/billable-info-by-job'),
  getResourcePlanningLines: (resourceNo, fromDate, toDate, debug) => {
    const p = new URLSearchParams();
    p.set('resource', resourceNo);
    if (fromDate) p.set('from', fromDate);
    if (toDate) p.set('to', toDate);
    if (debug) p.set('debug', '1');
    return request(`/api/timesheets/resource-planning-lines?${p.toString()}`);
  },

  // Invoices (Navision Web)
  getInvoices: (job, customer, debug) => {
    const p = new URLSearchParams();
    if (job) p.set('job', job);
    if (customer) p.set('customer', customer);
    if (debug) p.set('debug', '1');
    const qs = p.toString();
    return request(`/api/invoices${qs ? `?${qs}` : ''}`);
  },
  getInvoiceStatusLog: (invoiceNo, debug) =>
    request(`/api/invoices/${encodeURIComponent(invoiceNo)}/status-log${debug ? '?debug=1' : ''}`),
  getInvoiceDocImage: (invoiceNo, debug) =>
    request(`/api/invoices/${encodeURIComponent(invoiceNo)}/docimage${debug ? '?debug=1' : ''}`),
  postInvoiceStatus: (invoiceNo, status, effectiveDate, debug) =>
    request(`/api/invoices/${encodeURIComponent(invoiceNo)}/status${debug ? '?debug=1' : ''}`, { method: 'POST', body: JSON.stringify({ status, effectiveDate }) }),
  postInvoiceRevision: (invoiceNo, file, note, effectiveDate, jobNo, debug) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('note', note || '');
    if (effectiveDate) fd.append('effectiveDate', effectiveDate);
    if (jobNo) fd.append('jobNo', jobNo);
    return uploadFile(`/api/invoices/${encodeURIComponent(invoiceNo)}/revision${debug ? '?debug=1' : ''}`, fd);
  },
  postInvoiceExtraPages: (invoiceNo, file, jobNo, debug) => {
    const fd = new FormData();
    fd.append('file', file);
    if (jobNo) fd.append('jobNo', jobNo);
    return uploadFile(`/api/invoices/${encodeURIComponent(invoiceNo)}/extra-pages${debug ? '?debug=1' : ''}`, fd);
  },
  addInvoiceReceipts: (invoiceNo, jobNo, debug) =>
    request(`/api/invoices/${encodeURIComponent(invoiceNo)}/add-receipts${debug ? '?debug=1' : ''}`, { method: 'POST', body: JSON.stringify({ jobNo: jobNo || '' }) }),

  // Create Invoices wizard
  // all: every active job (Accounting Access users only; the server checks).
  getJobList: () => request('/api/create-invoices/job-list'),
  // Analytics > Reports
  getAnalyticsOptions: () => request('/api/analytics/options'),
  getAnalyticsReport: (params) => {
    const q = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => { if (v != null && v !== '') q.set(k, v); });
    return request(`/api/analytics/report?${q.toString()}`);
  },
  getReportFavorites: () => request('/api/analytics/favorites'),
  saveReportFavorite: (name, settings) => request('/api/analytics/favorites', { method: 'POST', body: JSON.stringify({ name, settings }) }),
  deleteReportFavorite: (id) => request(`/api/analytics/favorites/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  getJobHeader: (jobNo) => request(`/api/create-invoices/jobs/${encodeURIComponent(jobNo)}/header`),
  getCreateInvoiceJobs: (debug, all, job) => {
    const p = new URLSearchParams();
    if (debug) p.set('debug', '1');
    if (all) p.set('all', '1');
    if (job) p.set('job', job);
    const qs = p.toString();
    return request(`/api/create-invoices/jobs${qs ? `?${qs}` : ''}`);
  },
  // includeClosed: also return closed task lines (Invoice Approval "Show all").
  getCreateInvoiceTasks: (jobNo, debug, includeClosed) => {
    const p = new URLSearchParams();
    if (debug) p.set('debug', '1');
    if (includeClosed) p.set('includeClosed', '1');
    const qs = p.toString();
    return request(`/api/create-invoices/jobs/${encodeURIComponent(jobNo)}/tasks${qs ? `?${qs}` : ''}`);
  },
  getCreateInvoiceTimeSummary: (jobNo, debug) =>
    request(`/api/create-invoices/jobs/${encodeURIComponent(jobNo)}/time-summary${debug ? '?debug=1' : ''}`),
  getCreateInvoiceRateWarnings: (jobNo, debug) =>
    request(`/api/create-invoices/jobs/${encodeURIComponent(jobNo)}/rate-warnings${debug ? '?debug=1' : ''}`),
  // jobNo (when known) goes into the PDF's file name.
  getSyntheticInvoicePdfUrl: (docNo, jobNo) =>
    `/api/create-invoices/invoice/${encodeURIComponent(docNo)}/synthetic-pdf${jobNo ? `?job=${encodeURIComponent(String(jobNo).trim())}` : ''}`,
  // One expense entry's receipts as a PDF.
  planningLineReceiptsPdfUrl: (jobNo, jobTaskNo, lineNo, invoiceNo, posted) =>
    `/api/create-invoices/jobs/${encodeURIComponent(jobNo)}/planning-lines/${encodeURIComponent(lineNo)}/receipts-pdf?task=${encodeURIComponent(jobTaskNo)}`
    + `${invoiceNo ? `&inv=${encodeURIComponent(String(invoiceNo).trim())}` : ''}${posted ? '&posted=1' : ''}`,
  // recent: only the current month and the 3 months before it (server's today).
  getJobPlanningLines: (jobNo, taskNo, debug, { recent } = {}) => {
    const qs = new URLSearchParams();
    if (taskNo) qs.set('task', taskNo);
    if (recent) qs.set('recent', '1');
    if (debug) qs.set('debug', '1');
    const q = qs.toString();
    return request(`/api/create-invoices/jobs/${encodeURIComponent(jobNo)}/planning-lines${q ? `?${q}` : ''}`);
  },
  setPlanningLineNonBillable: (jobNo, lineNo, jobTaskNo, nonBillable) =>
    request(`/api/create-invoices/jobs/${encodeURIComponent(jobNo)}/planning-lines/${encodeURIComponent(lineNo)}/non-billable`,
      { method: 'POST', body: JSON.stringify({ jobTaskNo, nonBillable }) }),
  // Change a T&M line's rate (Work Type Code); an unposted-invoice line is
  // removed, changed and re-added by Navision. Returns { invoiceNo }.
  changePlanningLineWorkType: (jobNo, lineNo, jobTaskNo, workTypeCode) =>
    request(`/api/create-invoices/jobs/${encodeURIComponent(jobNo)}/planning-lines/${encodeURIComponent(lineNo)}/work-type`,
      { method: 'POST', body: JSON.stringify({ jobTaskNo, workTypeCode }) }),
  movePlanningLine: (jobNo, lineNo, jobTaskNo, newJobNo, newJobTaskNo) =>
    request(`/api/create-invoices/jobs/${encodeURIComponent(jobNo)}/planning-lines/${encodeURIComponent(lineNo)}/move`,
      { method: 'POST', body: JSON.stringify({ jobTaskNo, newJobNo, newJobTaskNo }) }),
  getJobPendingInvoices: (jobNo, debug) =>
    request(`/api/create-invoices/jobs/${encodeURIComponent(jobNo)}/pending-invoices${debug ? '?debug=1' : ''}`),
  deletePendingInvoice: (jobNo, docNo) =>
    request(`/api/create-invoices/jobs/${encodeURIComponent(jobNo)}/pending-invoices/${encodeURIComponent(docNo)}`,
      { method: 'DELETE' }),
  createInvoice: (jobNo, lines, comment, debug) =>
    request(`/api/create-invoices/jobs/${encodeURIComponent(jobNo)}/create${debug ? '?debug=1' : ''}`, { method: 'POST', body: JSON.stringify({ lines, comment: comment || '' }) }),
  // Budget tab (PM job-budget adjuster)
  getJobBudgets: (debug, includeClosed, job) => {
    const p = new URLSearchParams();
    if (debug) p.set('debug', '1');
    if (includeClosed) p.set('includeClosed', '1');
    if (job) p.set('job', job);
    const qs = p.toString();
    return request(`/api/job-budgets${qs ? `?${qs}` : ''}`);
  },
  adjustJobTaskBudget: (jobNo, jobTaskNo, payload, debug) =>
    request(`/api/job-budgets/jobs/${encodeURIComponent(jobNo)}/tasks/${encodeURIComponent(jobTaskNo)}/adjust${debug ? '?debug=1' : ''}`,
      { method: 'POST', body: JSON.stringify(payload || {}) }),
  setJobTaskAutoUnitIncrement: (jobNo, jobTaskNo, increment, debug) =>
    request(`/api/job-budgets/jobs/${encodeURIComponent(jobNo)}/tasks/${encodeURIComponent(jobTaskNo)}/auto-unit-increment${debug ? '?debug=1' : ''}`,
      { method: 'POST', body: JSON.stringify({ increment }) }),
  closeJobTask: (jobNo, jobTaskNo, debug) =>
    request(`/api/job-budgets/jobs/${encodeURIComponent(jobNo)}/tasks/${encodeURIComponent(jobTaskNo)}/close${debug ? '?debug=1' : ''}`,
      { method: 'POST', body: '{}' }),
  reopenJobTask: (jobNo, jobTaskNo, debug) =>
    request(`/api/job-budgets/jobs/${encodeURIComponent(jobNo)}/tasks/${encodeURIComponent(jobTaskNo)}/reopen${debug ? '?debug=1' : ''}`,
      { method: 'POST', body: '{}' }),

  // Invoice Approval tab
  getInvoiceApprovalJobs: (debug) =>
    request(`/api/invoice-approval/jobs${debug ? '?debug=1' : ''}`),
  getInvoiceApprovalJob: (jobNo, debug) =>
    request(`/api/invoice-approval/jobs/${encodeURIComponent(jobNo)}${debug ? '?debug=1' : ''}`),
  postInvoiceApprovalDecision: (jobNo, payload, debug) =>
    request(`/api/invoice-approval/jobs/${encodeURIComponent(jobNo)}/decision${debug ? '?debug=1' : ''}`,
      { method: 'POST', body: JSON.stringify(payload || {}) }),
  clearInvoiceApprovalJobStatus: (debug) =>
    request(`/api/invoice-approval/clear-job-status${debug ? '?debug=1' : ''}`, { method: 'POST', body: '{}' }),

  billableEmulate: (resourceNo, name) =>
    request('/api/timesheets/billable-emulate', { method: 'POST', body: JSON.stringify({ resourceNo, name }) }),
  getBillableColumns: () => request('/api/timesheets/billable-columns'),
  saveBillableColumns: (jobs) =>
    request('/api/timesheets/billable-columns', { method: 'PUT', body: JSON.stringify({ jobs }) }),

  // Time entries
  getTimeEntries: (timesheetId) =>
    request(`/api/time-entries?timesheetId=${timesheetId}`),
  getDailyTotal: (timesheetId, date) =>
    request(`/api/time-entries/daily-total?timesheetId=${timesheetId}&date=${date}`),
  createTimeEntry: (data) =>
    request('/api/time-entries', { method: 'POST', body: JSON.stringify(data) }),
  updateTimeEntry: (id, data) =>
    request(`/api/time-entries/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteTimeEntry: (id) =>
    request(`/api/time-entries/${id}`, { method: 'DELETE' }),
  deleteJobEntries: (timesheetId, jobId, jobTaskId) =>
    request(`/api/time-entries/by-job?timesheetId=${encodeURIComponent(timesheetId)}&jobId=${encodeURIComponent(jobId)}&jobTaskId=${encodeURIComponent(jobTaskId)}`, { method: 'DELETE' }),
  getPendingHours: (jobId, jobTaskId) =>
    request(`/api/time-entries/pending-hours?jobId=${encodeURIComponent(jobId)}&jobTaskId=${encodeURIComponent(jobTaskId)}`),
  moveEntries: (data) =>
    request('/api/time-entries/move', { method: 'PUT', body: JSON.stringify(data) }),

  // Favorites
  getFavorites: () => request('/api/favorites'),
  addFavorite: (jobId, jobTaskId) =>
    request('/api/favorites', { method: 'POST', body: JSON.stringify({ jobId, jobTaskId }) }),
  removeFavorite: (id) =>
    request(`/api/favorites/${id}`, { method: 'DELETE' }),
  reorderFavorites: (orderedIds) =>
    request('/api/favorites/reorder', { method: 'PUT', body: JSON.stringify({ orderedIds }) }),

  // Sync
  getSyncStatus: () => request('/api/sync/status'),
  checkSync: () => request('/api/sync/check', { method: 'POST' }),
  refreshSync: () => request('/api/sync/refresh', { method: 'POST' }),

  // Admin
  getResources: () => request('/api/admin/resources'),
  getResourcesLocal: () => request('/api/admin/resources-local'),
  getAppUsers: () => request('/api/admin/app-users'),
  getUsers: () => request('/api/admin/users'),
  toggleUserActive: (userId) => request(`/api/admin/users/${userId}/toggle-active`, { method: 'POST' }),
  emulateUser: (resourceNo, name, userId) =>
    request('/api/admin/emulate', { method: 'POST', body: JSON.stringify({ resourceNo, name, userId }) }),
  stopEmulation: () =>
    request('/api/admin/stop-emulate', { method: 'POST' }),
  getInvoiceReceiptsPdfUrl: (invoiceNo) => `/api/admin/invoice-receipts/${encodeURIComponent(invoiceNo)}/pdf`,
  // Invoice extras shown beside invoice links (any screen that shows invoices).
  getInvoiceReceiptCounts: (invoices) =>
    request('/api/invoices/receipt-counts', { method: 'POST', body: JSON.stringify({ invoices }) }),
  invoiceReceiptsPdfUrl: (invoiceNo, jobNo) =>
    `/api/invoices/${encodeURIComponent(invoiceNo)}/receipts-pdf${jobNo ? `?job=${encodeURIComponent(jobNo)}` : ''}`,
  invoiceTimeNotesPdfUrl: (invoiceNo, jobNo, posted) =>
    `/api/invoices/${encodeURIComponent(invoiceNo)}/time-notes-pdf?job=${encodeURIComponent(jobNo)}${posted ? '&posted=1' : ''}`,
  getNavResource: (email) => request(`/api/admin/nav-resource${email ? `?email=${encodeURIComponent(email)}` : ''}`),
  getEmailMappings: () => request('/api/admin/email-mappings'),
  addEmailMapping: (externalEmail, rcpEmail) => request('/api/admin/email-mappings', { method: 'POST', body: JSON.stringify({ externalEmail, rcpEmail }) }),
  deleteEmailMapping: (externalEmail) => request(`/api/admin/email-mappings/${encodeURIComponent(externalEmail)}`, { method: 'DELETE' }),
  refreshPermissions: () => request('/api/admin/refresh-permissions', { method: 'POST' }),
  setUserInvoicePrefix: (userId, hide) =>
    request(`/api/admin/users/${userId}/invoice-prefix`, { method: 'POST', body: JSON.stringify({ hide }) }),
  getAdminReportUsers: () => request('/api/expense-reports/admin-report-users'),
  getAdminReportJobs: (userId) => {
    const params = new URLSearchParams();
    if (userId) params.set('userId', userId);
    const qs = params.toString();
    return request(`/api/expense-reports/admin-report-jobs${qs ? `?${qs}` : ''}`);
  },
  getAdminCustomReportPdfUrl: ({ startDate, endDate, jobId, userId }) => {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (jobId) params.set('jobId', jobId);
    if (userId) params.set('userId', userId);
    return `/api/expense-reports/admin-custom-pdf?${params.toString()}`;
  },

  getRecentJobs: () => request('/api/jobs/recent'),

  // Expense Entries
  bulkAssignExpenseEntries: (data) =>
    request('/api/expense-entries/bulk-assign', { method: 'PUT', body: JSON.stringify(data) }),
  getExpenseEntries: () => request('/api/expense-entries'),
  getTopExpenseTypes: () =>
    request('/api/expense-entries/top-expense-types'),
  createExpenseEntry: (data, debug) =>
    request(`/api/expense-entries${debug ? '?debug=1' : ''}`, { method: 'POST', body: JSON.stringify(data) }),
  updateExpenseEntry: (id, data, debug) =>
    request(`/api/expense-entries/${id}${debug ? '?debug=1' : ''}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteExpenseEntry: (id, debug) =>
    request(`/api/expense-entries/${id}${debug ? '?debug=1' : ''}`, { method: 'DELETE' }),
  importCreditCardTransactions: (debug) =>
    request(`/api/expense-entries/import-credit-card${debug ? '?debug=1' : ''}`, { method: 'POST' }),
  splitExpenseEntry: (id) =>
    request(`/api/expense-entries/${id}/split`, { method: 'POST' }),
  addSubEntry: (headerId) =>
    request(`/api/expense-entries/${headerId}/add-sub`, { method: 'POST' }),
  getSubEntries: (headerId) =>
    request(`/api/expense-entries/${headerId}/sub-entries`),
  unsplitExpenseEntry: (headerId) =>
    request(`/api/expense-entries/${headerId}/unsplit`, { method: 'POST' }),
  getRecentSplits: () =>
    request('/api/expense-entries/recent-splits'),

  // Expense Reports
  syncExpenseReportStatuses: (debug) =>
    request(`/api/expense-reports/sync-status${debug ? '?debug=1' : ''}`, { method: 'POST' }),
  getExpenseReports: (page = 1, pageSize = 25) =>
    request(`/api/expense-reports?page=${page}&pageSize=${pageSize}`),
  getExpenseReport: (id) =>
    request(`/api/expense-reports/${id}`),
  submitExpenseReport: (entryIds, debug) =>
    request(`/api/expense-reports/submit${debug ? '?debug=1' : ''}`, { method: 'POST', body: JSON.stringify({ entryIds }) }),
  unsubmitExpenseReport: (id, debug) =>
    request(`/api/expense-reports/${id}/unsubmit${debug ? '?debug=1' : ''}`, { method: 'PUT' }),
  attachReceiptToReport: (reportId, { receiptId, expenseEntryId }, debug) =>
    request(`/api/expense-reports/${reportId}/receipts${debug ? '?debug=1' : ''}`, {
      method: 'POST',
      body: JSON.stringify({ receiptId, expenseEntryId }),
    }),
  removeReceiptFromReport: (reportId, receiptId, debug) =>
    request(`/api/expense-reports/${reportId}/receipts/${receiptId}${debug ? '?debug=1' : ''}`, {
      method: 'DELETE',
    }),
  getExpenseReportPdfUrl: (id) => `/api/expense-reports/${id}/pdf`,
  getCombinedReportPdfUrl: (ids) => `/api/expense-reports/combined-pdf?ids=${ids.map(encodeURIComponent).join(',')}`,
  getCustomReportPdfUrl: ({ startDate, endDate, jobId }) => {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (jobId) params.set('jobId', jobId);
    return `/api/expense-reports/custom-pdf?${params.toString()}`;
  },
  getReportJobs: () => request('/api/expense-reports/report-jobs'),

  // Expense Receipts
  getExpenseReceipts: () => request('/api/expense-receipts'),
  uploadReceipt: (file, expenseEntryId, debug) => {
    const formData = new FormData();
    formData.append('receipt', file);
    if (expenseEntryId) formData.append('expenseEntryId', expenseEntryId);
    const debugParam = debug ? '?debug=1' : '';
    return uploadFile(`/api/expense-receipts/upload${debugParam}`, formData);
  },
  assignReceipt: (receiptId, expenseEntryIds) => {
    // Accept array of entry IDs or null to unassign
    const body = Array.isArray(expenseEntryIds)
      ? { expenseEntryIds }
      : { expenseEntryId: expenseEntryIds };
    return request(`/api/expense-receipts/${receiptId}/assign`, { method: 'PUT', body: JSON.stringify(body) });
  },
  deleteReceipt: (id, debug) =>
    request(`/api/expense-receipts/${id}${debug ? '?debug=1' : ''}`, { method: 'DELETE' }),
  editReceiptImage: (id, blob, debug) => {
    const formData = new FormData();
    formData.append('image', blob, `receipt-${id}.jpg`);
    return uploadFile(`/api/expense-receipts/${id}/edit${debug ? '?debug=1' : ''}`, formData);
  },
  autoMatchReceipts: () =>
    request('/api/expense-receipts/auto-match', { method: 'POST' }),

  // Budgets (Navision Web)
  getBudgetPeriods: (debug) => request(`/api/budgets/periods${debug ? '?debug=1' : ''}`),
  getBudget: (period, debug) => request(`/api/budgets/${period}${debug ? '?debug=1' : ''}`),
  createBudget: (period, debug) => request(`/api/budgets/${period}${debug ? '?debug=1' : ''}`, { method: 'POST' }),
  unsubmitBudget: (period, debug) => request(`/api/budgets/${period}/unsubmit${debug ? '?debug=1' : ''}`, { method: 'POST' }),
  loadBudgetActuals: (period, debug) =>
    request(`/api/budgets/${period}/load-actuals${debug ? '?debug=1' : ''}`, { method: 'POST' }),
  // Budget Admin
  getBudgetAdminPeriods: () => request('/api/budgets/admin/periods'),
  getBudgetAdmin: (period) => request(`/api/budgets/admin/${period}`),
  loadBudgetAdminActuals: (period, debug) =>
    request(`/api/budgets/admin/${period}/load-actuals${debug ? '?debug=1' : ''}`, { method: 'POST' }),
  getBudgetAdminExportUrl: (period) => `/api/budgets/admin/${period}/export`,
  lockBudgetPeriod: (period) => request(`/api/budgets/admin/${period}/lock`, { method: 'POST' }),
  unlockBudgetPeriod: (period) => request(`/api/budgets/admin/${period}/unlock`, { method: 'POST' }),
  reconcileBudget: (period, debug) => request(`/api/budgets/${period}/reconcile${debug ? '?debug=1' : ''}`, { method: 'POST' }),
  saveBudget: (period, lines) =>
    request(`/api/budgets/${period}`, { method: 'PUT', body: JSON.stringify({ lines }) }),
  addBudgetOtherLine: (period) =>
    request(`/api/budgets/${period}/lines`, { method: 'POST', body: JSON.stringify({}) }),
  deleteBudgetLine: (period, lineGroupId) =>
    request(`/api/budgets/${period}/lines/${lineGroupId}`, { method: 'DELETE' }),
  submitBudget: (period, debug) => request(`/api/budgets/${period}/submit${debug ? '?debug=1' : ''}`, { method: 'POST' }),
  setBudgetMargin: (period, margin) => request(`/api/budgets/${period}/margin`, { method: 'PUT', body: JSON.stringify({ margin }) }),
  getBudgetExportUrl: (period) => `/api/budgets/${period}/export`,

  // Global budget settings (admin only)
  getBudgetSettings: () => request('/api/admin/budget-settings'),
  setBudgetHideNonJobsAndGoal: (hideNonJobsAndGoal) =>
    request('/api/admin/budget-settings', { method: 'POST', body: JSON.stringify({ hideNonJobsAndGoal }) }),
};

export default api;
